import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Trash2, Save, Image as ImageIcon, Film } from 'lucide-react';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import { HeroPC, HeroMobile } from '@/components/main/MainHero';
import { FEATURES, DEFAULT_CONTENT, mediaUrl } from '@/components/main/mainContent';
import { AdminFeaturePageEditor } from '@/components/main/AdminFeaturePageEditor';

const CHUNK = 4 * 1024 * 1024;
const IMG_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const VID_TYPES = ['video/mp4', 'video/webm'];

const uploadImage = async (file) => {
  if (!IMG_TYPES.includes(file.type)) throw new Error('JPG · PNG · WEBP 이미지만 등록할 수 있습니다.');
  if (file.size > 5 * 1024 * 1024) throw new Error('이미지는 5MB 이하만 등록할 수 있습니다.');
  const fd = new FormData();
  fd.append('file', file);
  const r = await api.post('/admin/main-page/upload-image', fd);
  return r.data.url;
};

const uploadVideo = async (file, onProgress) => {
  if (!VID_TYPES.includes(file.type)) throw new Error('MP4 · WEBM 영상만 등록할 수 있습니다.');
  if (file.size > 50 * 1024 * 1024) throw new Error('영상은 50MB 이하만 등록할 수 있습니다.');
  const uploadId = crypto.randomUUID().replace(/-/g, '');
  const total = Math.max(1, Math.ceil(file.size / CHUNK));
  let res;
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append('file', file.slice(i * CHUNK, (i + 1) * CHUNK), file.name);
    fd.append('upload_id', uploadId);
    fd.append('index', String(i));
    fd.append('total', String(total));
    fd.append('mime', file.type);
    res = await api.post('/admin/main-page/upload-video-chunk', fd);
    onProgress(Math.round(((i + 1) / total) * 100));
  }
  return res.data.url;
};

const errMsg = (e) => e?.response?.data?.detail || e?.message || '업로드 실패';

const MediaSlot = ({ label, kind, value, onChange, testid, removable }) => {
  const [busy, setBusy] = useState(null);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(0);
    try {
      const url = kind === 'video' ? await uploadVideo(file, setBusy) : await uploadImage(file);
      onChange(url);
      toast.success(`${label} 업로드 완료 · 저장을 눌러야 홈페이지에 반영됩니다.`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(null); }
  };
  const src = mediaUrl(value);
  return (
    <div className="rounded-xl border border-black/10 p-3 bg-white" data-testid={testid}>
      <p className="text-xs font-bold text-gray-700 mb-2 flex items-center gap-1.5">
        {kind === 'video' ? <Film className="w-3.5 h-3.5" /> : <ImageIcon className="w-3.5 h-3.5" />}{label}
      </p>
      <div className="aspect-video rounded-lg bg-gray-100 overflow-hidden flex items-center justify-center">
        {src ? (kind === 'video'
          ? <video src={src} muted playsInline controls className="w-full h-full object-contain bg-black" />
          : <img src={src} alt={label} className="w-full h-full object-cover" />)
          : <span className="text-[11px] text-gray-400">미등록</span>}
      </div>
      <div className="mt-2 flex gap-2">
        <label className="flex-1 inline-flex items-center justify-center gap-1 h-8 rounded-lg bg-[#1B2140] text-white text-xs font-bold cursor-pointer">
          <Upload className="w-3.5 h-3.5" />
          {busy !== null ? (kind === 'video' ? `업로드 ${busy}%` : '업로드 중…') : (src ? '교체' : '등록')}
          <input type="file" className="hidden" disabled={busy !== null} onChange={pick}
            accept={(kind === 'video' ? VID_TYPES : IMG_TYPES).join(',')} data-testid={`${testid}-input`} />
        </label>
        {removable && src && (
          <button type="button" onClick={() => onChange(null)} className="h-8 px-2.5 rounded-lg border text-xs"
            data-testid={`${testid}-remove`}><Trash2 className="w-3.5 h-3.5" /></button>
        )}
      </div>
    </div>
  );
};

const Preview = ({ draft }) => (
  <div className="flex flex-wrap gap-6 items-start" data-testid="main-admin-preview">
    <div>
      <p className="text-xs font-bold text-gray-600 mb-1">PC 미리보기</p>
      <div className="w-[576px] max-w-full h-[256px] overflow-hidden rounded-lg border">
        <div style={{ width: 1440, transform: 'scale(0.4)', transformOrigin: 'top left' }}>
          <HeroPC content={draft} height={640} onPlay={() => {}} />
        </div>
      </div>
    </div>
    <div>
      <p className="text-xs font-bold text-gray-600 mb-1">모바일 미리보기</p>
      <div className="w-[195px] h-[390px] overflow-hidden rounded-lg border">
        <div style={{ width: 390, transform: 'scale(0.5)', transformOrigin: 'top left' }}>
          <HeroMobile content={draft} onPlay={() => {}} />
        </div>
      </div>
    </div>
  </div>
);

export default function AdminMainPage({ user }) {
  const [tab, setTab] = useState('main');
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get('/admin/main-page').then((r) => setDraft(r.data))
      .catch((e) => { toast.error(errMsg(e)); setDraft(DEFAULT_CONTENT); });
  }, []);
  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));
  const setCard = (k, v) => setDraft((d) => ({ ...d, card_images: { ...(d.card_images || {}), [k]: v } }));
  const hasVideo = !!(draft?.hero_video_pc || draft?.hero_video_mobile);

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/admin/main-page', draft);
      setDraft(r.data);
      toast.success('저장 완료 · 메인 홈페이지에 반영되었습니다.');
    } catch (e) { toast.error(typeof errMsg(e) === 'string' ? errMsg(e) : '저장 실패'); } finally { setSaving(false); }
  };

  return (
    <AdminLayout user={user} active="main-page">
      <div className="px-4 md:px-6 pt-4 md:pt-6 flex gap-2" data-testid="main-admin-tabs">
        {[['main', '메인 홍보 콘텐츠'], ['feature', '기능 상세 페이지'], ['self-live', '자사 LIVE 소개 페이지']].map(([k, l]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`main-admin-tab-${k}`}
            className={`h-9 px-4 rounded-lg text-sm font-bold border ${tab === k ? 'bg-[#1B2140] text-white' : 'bg-white'}`}>{l}</button>
        ))}
      </div>
      {tab === 'self-live' ? (
        <div className="p-4 md:p-6 max-w-6xl">
          <AdminFeaturePageEditor slug="why-self-live" path="/why-self-live" MediaSlot={MediaSlot} hideBenefits taglineLabel="하단 마무리 문구" />
        </div>
      ) : tab === 'feature' ? (
        <div className="p-4 md:p-6 max-w-6xl">
          <AdminFeaturePageEditor slug="live-console" path="/features/live-console" MediaSlot={MediaSlot} />
        </div>
      ) : !draft ? <p className="p-6 text-sm text-gray-500">불러오는 중…</p> : (
        <div className="p-4 md:p-6 space-y-6 max-w-6xl" data-testid="admin-main-page">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-xl font-black">메인 홈페이지 홍보 콘텐츠</h1>
              <p className="text-xs text-gray-500 mt-1">이미지 JPG·PNG·WEBP 5MB 이하 · 영상 MP4·WEBM 50MB 이하 · 영상은 음소거 자동재생, 재생 실패 시 이미지 표시</p>
            </div>
            <button type="button" onClick={save} disabled={saving} data-testid="main-admin-save-btn"
              className="inline-flex items-center gap-1.5 h-10 px-5 rounded-lg bg-[#2F5BFF] text-white text-sm font-bold disabled:opacity-50">
              <Save className="w-4 h-4" />{saving ? '저장 중…' : '저장'}
            </button>
          </div>

          <div className="rounded-xl border p-4 bg-white" data-testid="main-admin-mode">
            <p className="text-sm font-bold mb-2">콘텐츠 사용 설정</p>
            <div className="flex gap-2">
              {[['image', '이미지 사용'], ['video', '영상 사용']].map(([v, l]) => (
                <button key={v} type="button" data-testid={`main-admin-mode-${v}`}
                  disabled={v === 'video' && !hasVideo}
                  onClick={() => set('hero_mode', v)}
                  className={`h-9 px-4 rounded-lg text-sm font-bold border disabled:opacity-40 ${draft.hero_mode === v ? 'bg-[#1B2140] text-white' : 'bg-white'}`}>
                  {l}
                </button>
              ))}
            </div>
            {!hasVideo && <p className="text-[11px] text-gray-500 mt-2">영상을 먼저 등록하면 「영상 사용」을 선택할 수 있습니다.</p>}
          </div>

          <div className="rounded-xl border p-4 bg-white space-y-3" data-testid="main-admin-self-live-banner">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold">자사 LIVE 홍보 배너 (홍보 영역 아래)</p>
              <label className="flex items-center gap-1.5 text-sm font-bold">
                <input type="checkbox" checked={draft.self_live_banner_visible !== false}
                  onChange={(e) => set('self_live_banner_visible', e.target.checked)} data-testid="main-admin-banner-visible" /> 표시
              </label>
            </div>
            <input value={draft.self_live_banner_title || ''} maxLength={120} onChange={(e) => set('self_live_banner_title', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="배너 제목" data-testid="main-admin-banner-title" />
            <textarea rows={2} value={draft.self_live_banner_desc || ''} maxLength={300} onChange={(e) => set('self_live_banner_desc', e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="배너 설명" data-testid="main-admin-banner-desc" />
            <p className="text-[11px] text-gray-500">상세 페이지 이미지·영상·소개 문구는 「자사 LIVE 소개 페이지」 탭에서 관리합니다.</p>
          </div>

          <div className="rounded-xl border p-4 bg-white grid md:grid-cols-[1fr_260px] gap-4" data-testid="main-admin-intro">
            <div>
              <p className="text-sm font-bold mb-2">왼쪽 소개 영역 표시 방식</p>
              <div className="flex gap-2">
                {[['text', '문구형'], ['image', '이미지형']].map(([v, l]) => (
                  <button key={v} type="button" data-testid={`main-admin-intro-mode-${v}`}
                    disabled={v === 'image' && !draft.intro_image}
                    onClick={() => set('intro_mode', v)}
                    className={`h-9 px-4 rounded-lg text-sm font-bold border disabled:opacity-40 ${(draft.intro_mode || 'text') === v ? 'bg-[#1B2140] text-white' : 'bg-white'}`}>
                    {l}
                  </button>
                ))}
              </div>
              <p className="text-[11px] text-gray-500 mt-2">이미지형에서도 「지금 무료로 시작하기」는 이미지 아래 실제 버튼으로 표시됩니다. 이미지 안에 버튼을 그리지 마세요. PC·모바일 공통 · 세로형 또는 정사각형 권장.</p>
            </div>
            <MediaSlot label="소개 영역 이미지" kind="image" removable value={draft.intro_image}
              onChange={(v) => setDraft((d) => ({ ...d, intro_image: v, intro_mode: v ? d.intro_mode : 'text' }))} testid="main-admin-intro-image" />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <MediaSlot label="PC 홍보 이미지" kind="image" value={draft.hero_image_pc} onChange={(v) => set('hero_image_pc', v)} testid="main-admin-hero-image-pc" />
            <MediaSlot label="모바일 홍보 이미지" kind="image" value={draft.hero_image_mobile} onChange={(v) => set('hero_image_mobile', v)} testid="main-admin-hero-image-mobile" />
            <MediaSlot label="PC 홍보영상" kind="video" removable value={draft.hero_video_pc}
              onChange={(v) => setDraft((d) => ({ ...d, hero_video_pc: v, hero_mode: !v && !d.hero_video_mobile ? 'image' : d.hero_mode }))} testid="main-admin-hero-video-pc" />
            <MediaSlot label="모바일 홍보영상" kind="video" removable value={draft.hero_video_mobile}
              onChange={(v) => setDraft((d) => ({ ...d, hero_video_mobile: v, hero_mode: !v && !d.hero_video_pc ? 'image' : d.hero_mode }))} testid="main-admin-hero-video-mobile" />
          </div>

          <div className="rounded-xl border p-4 bg-white"><p className="text-sm font-bold mb-3">미리보기 (저장 전 상태)</p><Preview draft={draft} /></div>

          <div>
            <p className="text-sm font-bold mb-2">주요 기능 카드 이미지</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {FEATURES.map((f) => (
                <MediaSlot key={f.key} label={f.title} kind="image" removable value={draft.card_images?.[f.key]}
                  onChange={(v) => setCard(f.key, v)} testid={`main-admin-card-${f.key}`} />
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
